import React, { useState } from 'react';
import './TopWidgetEdit.css';
import './PersonalBrandingWidgetEdit.css';
import { useWidgetEdit } from '../hooks/useWidgetEdit';
import ImageContainer from '../WidgetEditComponents/ImageContainer';
import TextSettingsSection from '../WidgetEditComponents/TextSettingsSection';
import MoreSettingsSection from '../WidgetEditComponents/MoreSettingsSection';
import AdvancedSettingsSection from '../WidgetEditComponents/AdvancedSettingsSection';
import AnimationSettingsSection from '../WidgetEditComponents/AnimationSettingsSection';
import LayoutSelector from '../WidgetEditComponents/LayoutSelector';
import PersonalBrandingBulletsManagement from '../WidgetEditComponents/PersonalBrandingBulletsManagement';
import ButtonWidgetStyle from '../WidgetEditComponents/ButtonWidgetStyle';
import RevolutionaryLayoutSettings from '../WidgetEditComponents/RevolutionaryLayoutSettings';
import SaveTemplatePopup from '../WidgetEditComponents/SaveTemplatePopup';

function PersonalBrandingWidgetEdit({ widget, onUpdate, colors }) {
    const { editData, handleInputChange, uploadImage, handleSliderChange } = useWidgetEdit(widget, onUpdate);
    
    // Image Upload States
    const [isUploadingProfile, setIsUploadingProfile] = useState(false);
    
    // ✅ SAVE TEMPLATE POPUP STATE
    const [showSaveTemplatePopup, setShowSaveTemplatePopup] = useState(false);

    // Bullet Point Management
    const addBulletPoint = () => {
        const currentBullets = editData.bulletPoints || [];
        const newBullet = {
            id: `bp${Date.now()}`,
            text: 'New bullet point text...'
        };
        const updatedBullets = [...currentBullets, newBullet];
        handleInputChange({ target: { name: 'bulletPoints', value: updatedBullets } });
    };

    const removeBulletPoint = (bulletId) => {
        const currentBullets = editData.bulletPoints || [];
        const updatedBullets = currentBullets.filter(bullet => bullet.id !== bulletId);
        handleInputChange({ target: { name: 'bulletPoints', value: updatedBullets } });
    };

    const updateBulletPoint = (bulletId, newText) => {
        const currentBullets = editData.bulletPoints || [];
        const updatedBullets = currentBullets.map(bullet => 
            bullet.id === bulletId ? { ...bullet, text: newText } : bullet
        );
        handleInputChange({ target: { name: 'bulletPoints', value: updatedBullets } });
    };

    // Layout Change Handler
    const handleLayoutChange = (layoutId, layoutConfig) => {
        handleInputChange({ target: { name: 'containerLayout', value: layoutId } });
    };

    // 🚀 REVOLUTIONARY TEXT SETTINGS - Alle Text-Felder mit Show/Hide Toggles
    const textConfig = [
        {
            fieldName: 'overlayName',
            label: 'Name (above image)',
            type: 'input',
            placeholder: 'Hi, I\'m Emma',
            fontField: 'overlayNameFont',
            fontType: 'big',
            effectField: 'overlayNameEffect'
        },
        {
            fieldName: 'overlayTitle1',
            label: 'Title 1 (right side)',
            type: 'input',
            placeholder: 'Manifestation Expert.',
            fontField: 'overlayTitleFont',
            fontType: 'small',
            effectField: 'overlayTitleEffect'
        },
        {
            fieldName: 'overlayTitle2',
            label: 'Title 2 (right side)',
            type: 'input',
            placeholder: 'Bestselling author. Podcaster.',
            fontField: 'overlayTitleFont',
            fontType: 'small',
            effectField: 'overlayTitleEffect'
        },
        {
            fieldName: 'overlayTitle3',
            label: 'Title 3 (right side)',
            type: 'input',
            placeholder: 'Spiritual Queen.',
            fontField: 'overlayTitleFont',
            fontType: 'small',
            effectField: 'overlayTitleEffect'
        },
        {
            fieldName: 'mainQuestion',
            label: 'Main Question',
            type: 'textarea',
            placeholder: 'Do you feel like your dream life is always just out of reach?',
            rows: 3,
            fontField: 'mainQuestionFont',
            fontType: 'big',
            effectField: 'mainQuestionEffect'
        },
        {
            fieldName: 'helpText',
            label: 'Help Text',
            type: 'input',
            placeholder: 'I can help you:',
            fontField: 'helpTextFont',
            fontType: 'small'
        },
        {
            fieldName: 'imageDescription',
            label: 'Image Description',
            type: 'textarea',
            placeholder: 'Emma Mumford is the UK\'s leading Manifestation expert...',
            rows: 4,
            fontField: 'imageDescriptionFont',
            fontType: 'small'
        },
    ];


    // Color Picker Configuration
    const colorConfig = [
        [
            {
                fieldName: 'overlayNameColor',
                label: 'Overlay Name',
                defaultValue: '#8B7355',
                icon: '/img/text-fat.svg'
            },
            {
                fieldName: 'overlayTitleColor',
                label: 'Overlay Titles',
                defaultValue: '#6B6B6B',
                icon: '/img/text.svg'
            },
            {
                fieldName: 'mainQuestionColor',
                label: 'Main Question',
                defaultValue: '#4A4A4A',
                icon: '/img/text-fat.svg'
            }
        ],
        [
            {
                fieldName: 'helpTextColor',
                label: 'Help Text',
                defaultValue: '#666666',
                icon: '/img/text.svg'
            },
            {
                fieldName: 'bulletPointColor',
                label: 'Bullet Points',
                defaultValue: '#5A5A5A',
                icon: '/img/text.svg'
            },
            {
                fieldName: 'imageDescriptionColor',
                label: 'Image Description',
                defaultValue: '#666666',
                icon: '/img/text.svg'
            }
        ],
        [
            {
                fieldName: 'backgroundColor',
                label: 'Background',
                defaultValue: '#F5F5F0',
                icon: '/img/background.svg'
            }
        ]
    ];

    // Font Settings Configuration
    const fontConfig = [
        {
            label: 'Overlay Name',
            sizeField: 'overlayNameSize',
            weightField: 'overlayNameWeight',
            defaultSize: '36',
            defaultWeight: '400'
        },
        {
            label: 'Overlay Titles',
            sizeField: 'overlayTitleSize',
            weightField: 'overlayTitleWeight',
            defaultSize: '20',
            defaultWeight: '400'
        },
        {
            label: 'Main Question',
            sizeField: 'mainQuestionSize',
            weightField: 'mainQuestionWeight',
            defaultSize: '32',
            defaultWeight: '400'
        },
        {
            label: 'Help Text',
            sizeField: 'helpTextSize',
            weightField: 'helpTextWeight',
            defaultSize: '16',
            defaultWeight: '400'
        },
        {
            label: 'Bullet Points',
            sizeField: 'bulletPointSize',
            weightField: 'bulletPointWeight',
            defaultSize: '14',
            defaultWeight: '400'
        },
        {
            label: 'Image Description',
            sizeField: 'imageDescriptionSize',
            weightField: 'imageDescriptionWeight',
            defaultSize: '12',
            defaultWeight: '400'
        },
    ];

    // 🚀 REVOLUTIONARY LAYOUT SETTINGS - Custom Properties für Personal Branding Widget
    const customLayoutProperties = [
        {
            section: 'Container Settings',
            icon: 'fa-window-maximize',
            properties: [
                {
                    id: 'containerPadding',
                    label: 'Container Padding',
                    min: 20,
                    max: 60,
                    step: 5,
                    unit: 'px',
                    icon: '📦',
                    description: 'Inner padding of the main container'
                }
            ]
        },
        {
            section: 'Content Settings',
            icon: 'fa-align-left',
            properties: [
                {
                    id: 'contentSpacing',
                    label: 'Content Spacing',
                    min: 20,
                    max: 80,
                    step: 5,
                    unit: 'px',
                    icon: '📄',
                    description: 'Spacing between content sections'
                },
                {
                    id: 'bulletSpacing',
                    label: 'Bullet Spacing',
                    min: 8,
                    max: 32,
                    step: 2,
                    unit: 'px',
                    icon: '📋',
                    description: 'Spacing between bullet points'
                }
            ]
        }
    ];

    // 🔧 ADVANCED SETTINGS CONFIGURATION - Nur noch Widget-spezifische Properties
    const advancedConfig = [

        // Button Group  
        {
            fieldName: 'buttonBorderRadius',
            label: 'Button Radius',
            type: 'number',
            defaultValue: 4,
            min: 0,
            max: 30,
            step: 2,
            unit: 'px',
            icon: '/img/element.svg'
        },
        {
            fieldName: 'buttonPaddingY',
            label: 'Button Height',
            type: 'number',
            defaultValue: 16,
            min: 8,
            max: 32,
            step: 2,
            unit: 'px',
            icon: '/img/element.svg'
        },
        {
            fieldName: 'buttonPaddingX',
            label: 'Button Padding X',
            type: 'number',
            defaultValue: 32,
            min: 16,
            max: 64,
            step: 4,
            unit: 'px',
            icon: '/img/element.svg'
        },
        {
            fieldName: 'buttonMinWidth',
            label: 'Button Min Width',
            type: 'number',
            defaultValue: 250,
            min: 150,
            max: 400,
            step: 10,
            unit: 'px',
            icon: '/img/element.svg'
        },
        // Animation Group
        {
            fieldName: 'animationDuration',
            label: 'Animation Speed',
            type: 'slider',
            defaultValue: 600,
            min: 300,
            max: 1200,
            step: 100,
            unit: 'ms',
            icon: '/img/element.svg',
            color: '#9C27B0',
            description: 'Duration of entrance animations'
        },
        {
            fieldName: 'animationDelay',
            label: 'Animation Delay',
            type: 'slider',
            defaultValue: 150,
            min: 50,
            max: 400,
            step: 50,
            unit: 'ms',
            icon: '/img/element.svg',
            color: '#FF5722',
            description: 'Delay before animations start'
        }
    ];

    return (
        <div>
            <div className="top-widget-edit-container">
                
                {/* Container Layout Selector - ALWAYS AT THE TOP */}
                <div className="widget-trenner">
                    <h3>Container Layout</h3>
                    <hr className="hrrrr" />
                </div>
                
                <LayoutSelector
                    widgetType="personalBrandingWidget"
                    currentLayout={editData.containerLayout || 'glass-card'}
                    selectedLayout={editData.containerLayout || 'glass-card'}
                    onLayoutChange={handleLayoutChange}
                />

                {/* 🚀 PROFILE IMAGE CONTAINER - direkt nach Layouts */}
                <ImageContainer
                    label="Profile Image"
                    fieldName="profileImage"
                    currentImage={editData.profileImage}
                    onUpload={uploadImage}
                    isUploading={isUploadingProfile}
                    setIsUploading={setIsUploadingProfile}
                    controls={[
                        {
                            type: 'size',
                            label: 'Image Height',
                            fieldName: 'profileImageHeight',
                            defaultValue: 600,
                            min: 400,
                            max: 800,
                            step: 20,
                            unit: 'px',
                            description: 'Height of the profile image'
                        }
                    ]}
                    editData={editData}
                    handleInputChange={handleInputChange}
                    theme="default"
                />
                
                {/* Bullet Point Style Selector */}
                {editData.bulletPoints && editData.bulletPoints.length > 0 && (
                    <>
                        <div className="widget-trenner">
                            <h3>Bullet Point Style</h3>
                            <hr className="hrrrr" />
                        </div>
                        
                        <div className="bullet-style-selector">
                            {[
                                { id: 'elegant', name: 'Elegant', icon: '✓', description: 'Glass cards with checkmarks', color: '#D4A574' },
                                { id: 'modern', name: 'Modern', icon: '', description: 'Clean line with left border', color: '#2196F3' },
                                { id: 'luxury', name: 'Luxury', icon: '♔', description: 'Crown with gradient', color: '#9C27B0' },
                                { id: 'minimal', name: 'Minimal', icon: '●', description: 'Simple dots', color: '#607D8B' },
                                { id: 'default', name: 'Default', icon: '+', description: 'Plus signs', color: '#795548' }
                            ].map(style => (
                                <div
                                    key={style.id}
                                    className={`bullet-style-option ${(editData.bulletPointStyle || 'elegant') === style.id ? 'active' : ''}`}
                                    data-style={style.id}
                                    onClick={() => handleInputChange({ target: { name: 'bulletPointStyle', value: style.id } })}
                                >
                                    <div className="bullet-style-preview">
                                        <div 
                                            className="bullet-style-icon"
                                            style={{ color: style.color }}
                                        >
                                            {style.icon}
                                        </div>
                                        <div className="bullet-style-lines">
                                            <div className="bullet-style-line"></div>
                                            <div className="bullet-style-line short"></div>
                                        </div>
                                    </div>
                                    <div className="bullet-style-info">
                                        <h4 className="bullet-style-name">{style.name}</h4>
                                        <p className="bullet-style-description">{style.description}</p>
                                    </div>
                                    {(editData.bulletPointStyle || 'elegant') === style.id && (
                                        <div className="bullet-style-selected">
                                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                                <path 
                                                    d="M13.5 4.5L6 12L2.5 8.5" 
                                                    stroke="currentColor" 
                                                    strokeWidth="2" 
                                                    strokeLinecap="round" 
                                                    strokeLinejoin="round"
                                                />
                                            </svg>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                        
                        {/* Bullet Point Design Selector */}
                        <div className="widget-trenner">
                            <h3>Bullet Point Design</h3>
                            <hr className="hrrrr" />
                        </div>
                        
                        <div className="bullet-design-selector">
                            {[
                                { id: 'default', name: 'Default', description: 'Simple text bullets', preview: '▪▪▪' },
                                { id: 'cards', name: 'Cards', description: 'Each bullet in own card', preview: '□□□' },
                                { id: 'bordered', name: 'Bordered', description: 'Bullets with borders', preview: '▢▢▢' },
                                { id: 'highlighted', name: 'Highlighted', description: 'Background highlights', preview: '▨▨▨' },
                                { id: 'rounded', name: 'Rounded', description: 'Rounded pill containers', preview: '◯◯◯' },
                                { id: 'gradient', name: 'Gradient', description: 'Gradient background cards', preview: '🌈🌈🌈' },
                                { id: 'vintage', name: 'Vintage', description: 'Retro paper style', preview: '▦▦▦' }
                            ].map(design => (
                                <div
                                    key={design.id}
                                    className={`bullet-design-option ${(editData.bulletPointDesign || 'default') === design.id ? 'active' : ''}`}
                                    data-design={design.id}
                                    onClick={() => handleInputChange({ target: { name: 'bulletPointDesign', value: design.id } })}
                                >
                                    <div className="bullet-design-preview">
                                        <span className="bullet-design-symbol">{design.preview}</span>
                                    </div>
                                    <div className="bullet-design-info">
                                        <h4 className="bullet-design-name">{design.name}</h4>
                                        <p className="bullet-design-description">{design.description}</p>
                                    </div>
                                    {(editData.bulletPointDesign || 'default') === design.id && (
                                        <div className="bullet-design-selected">
                                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                                <path 
                                                    d="M13.5 4.5L6 12L2.5 8.5" 
                                                    stroke="currentColor" 
                                                    strokeWidth="2" 
                                                    strokeLinecap="round" 
                                                    strokeLinejoin="round"
                                                />
                                            </svg>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </>
                )}
                

                {/* REMOVED: Container Padding moved to Advanced Settings */}

                {/* 🚀 REVOLUTIONARY TEXT SETTINGS - Alle Text-Felder mit Show/Hide Toggles */}
                <TextSettingsSection 
                    textConfig={textConfig}
                    editData={editData}
                    handleInputChange={handleInputChange}
                    showFontSelector={true}
                    showTextEffects={true}
                    showButtonEffects={true}
                />

                {/* 🚀 REVOLUTIONARY BUTTON STYLE SECTION */}
                <ButtonWidgetStyle 
                    editData={editData}
                    handleInputChange={handleInputChange}
                    buttonConfig={{
                        textField: 'buttonText',
                        linkField: 'buttonLink'
                    }}
                />

                {/* Personal Branding Bullet Points Management */}
                <PersonalBrandingBulletsManagement 
                    bulletPoints={editData.bulletPoints || []}
                    addBulletPoint={addBulletPoint}
                    removeBulletPoint={removeBulletPoint}
                    updateBulletPoint={updateBulletPoint}
                    editData={editData}
                />

                {/* OLD Dynamic Bullet Points Management */}
                <div className="widget-trenner" style={{ display: 'none' }}>
                    <h3>Bullet Points</h3>
                    <hr className="hrrrr" />
                </div>

                <div className="elegant-input-container" style={{ display: 'none' }}>
                    <div className="elegant-input-header">
                        <div className="elegant-label-row">
                            <label className="elegant-input-label">Manage Bullet Points</label>
                            <button 
                                type="button"
                                onClick={addBulletPoint}
                                style={{
                                    backgroundColor: '#5C80BC',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    padding: '6px 12px',
                                    fontSize: '12px',
                                    cursor: 'pointer',
                                    fontWeight: '600'
                                }}
                                title="Add new bullet point"
                            >
                                + Add Bullet
                            </button>
                        </div>
                    </div>
                    
                    {/* Render existing bullet points */}
                    <div style={{ marginTop: '12px' }}>
                        {(editData.bulletPoints || []).map((bullet, index) => (
                            <div key={bullet.id} style={{
                                display: 'flex',
                                gap: '8px',
                                marginBottom: '12px',
                                alignItems: 'flex-start'
                            }}>
                                <textarea
                                    value={bullet.text}
                                    onChange={(e) => updateBulletPoint(bullet.id, e.target.value)}
                                    className="widgetDescriptionField elegant-textarea"
                                    placeholder="Enter bullet point text..."
                                    rows={2}
                                    style={{ flex: 1 }}
                                />
                                <button
                                    type="button"
                                    onClick={() => removeBulletPoint(bullet.id)}
                                    style={{
                                        backgroundColor: '#dc3545',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        padding: '8px',
                                        fontSize: '12px',
                                        cursor: 'pointer',
                                        marginTop: '4px'
                                    }}
                                    title="Remove bullet point"
                                >
                                    ×
                                </button>
                            </div>
                        ))}
                        
                        {(!editData.bulletPoints || editData.bulletPoints.length === 0) && (
                            <p style={{
                                color: '#666',
                                fontStyle: 'italic',
                                textAlign: 'center',
                                padding: '20px'
                            }}>
                                No bullet points yet. Click "Add Bullet" to create one.
                            </p>
                        )}
                    </div>
                </div>

                {/* 🚀 REVOLUTIONARY LAYOUT SETTINGS - Standard + Custom Properties */}
                <RevolutionaryLayoutSettings 
                    editData={editData}
                    handleInputChange={handleInputChange}
                    customProperties={customLayoutProperties}
                />

                {/* Animation Settings - Universal Component */}
                <AnimationSettingsSection 
                    editData={editData}
                    onUpdate={onUpdate}
                    showSpeedControls={true}
                    showDelayControls={true}
                    defaultExpanded={false}
                />

                {/* 🔧 ADVANCED SETTINGS - Nur noch Widget-spezifische Properties */}
                <AdvancedSettingsSection 
                    advancedConfig={advancedConfig}
                    editData={editData}
                    handleInputChange={handleInputChange}
                    handleSliderChange={handleSliderChange}
                    defaultExpanded={false}
                />

                {/* More Settings (Colors & Fonts only) */}
                <MoreSettingsSection 
                    colors={colors}
                    colorConfig={colorConfig}
                    fontConfig={fontConfig}
                    layoutConfig={[]} // Empty - moved to Revolutionary Layout Settings
                    editData={editData}
                    handleInputChange={handleInputChange}
                />

                {/* 💾 SAVE AS TEMPLATE BUTTON */}
                <div className="save-template-section">
                    <button 
                        className="save-template-btn"
                        onClick={() => setShowSaveTemplatePopup(true)}
                        title="Save this widget configuration as a reusable template"
                    >
                        <i className="fas fa-save"></i>
                        Save as Template
                    </button>
                </div>

                {/* 💾 SAVE TEMPLATE POPUP */}
                <SaveTemplatePopup
                    isOpen={showSaveTemplatePopup}
                    onClose={() => setShowSaveTemplatePopup(false)}
                    widgetData={editData}
                    originalWidgetType={widget.type}
                />
                
            </div>
        </div>
    );
}

export default PersonalBrandingWidgetEdit;
